import React, { useContext, useEffect, useState } from 'react';

import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { FiDownload } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';

import { AppButton } from '../../../components/AppButton';
import { DeviceContext } from '../../../contexts';
import { Team } from '../../../types';
import { MAIN_CONTAINER_ID } from '../../../constants';
import { REPORTS_ROUTE } from '../constants';
import {
  ReportWizardStep,
  useRequestReportWizard,
  UseRequestReportWizardProps,
} from '../hooks/useRequestReportWizard';
import { useReportDownload } from '../hooks/useReportDownload';
import { useReportStyles } from '../styles';
import { ReportStatusPanel } from './ReportStatusPanel';
import {
  AnalysisStep,
  ContentStep,
  PeopleStep,
  QualificationsStep,
  ReviewStep,
  ScopeStep,
  TypeStep,
} from './WizardSteps';

const TITLES: Record<ReportWizardStep, string> = {
  type: 'What kind of report?',
  scope: 'Who should this cover?',
  select: 'Select team members',
  content: 'What should it include?',
  qualifications: 'Which qualifications?',
  analysis: 'Analyse this report?',
  review: 'Review & generate',
};

interface Props extends Omit<UseRequestReportWizardProps, 'open'> {
  open: boolean;
  onClose: () => void;
  teams: Team[];
  companyName?: string;
}

/**
 * Request a Teams report: a company/team snapshot or an individual report, with optional
 * analysis. Generation runs in the background; the dialog polls while it stays open and the
 * Reports page carries on after it is closed.
 */
export const RequestReportDialog: React.FC<Props> = ({ open, onClose, teams, companyName, ...props }) => {
  const classes = useReportStyles();
  const { isMobile } = useContext(DeviceContext);
  const history = useHistory();
  const wizard = useRequestReportWizard({ open, ...props });
  const { download, downloadingId } = useReportDownload(props.companyId);

  const submitted = !!wizard.requestId;
  const { report, timedOut, error: pollError } = wizard.polling;
  // The Download button waits for the mark to land, like the copy above it
  const [markLanded, setMarkLanded] = useState(false);
  useEffect(() => setMarkLanded(false), [wizard.requestId]);
  const isLastStep = wizard.step === 'review';

  const goToReports = () => {
    onClose();
    history.push(wizard.requestId ? `${REPORTS_ROUTE}/${wizard.requestId}` : REPORTS_ROUTE);
  };

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      classes={{ paper: classes.dialogPaper }}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      open={open}
      aria-labelledby="request-report-title"
      onClose={(event, reason) => {
        // A half-filled wizard is not thrown away by a stray click outside it
        if (submitted || (reason !== 'backdropClick' && reason !== 'escapeKeyDown')) onClose();
      }}>
      {!submitted && (
        <>
          <DialogTitle disableTypography className={classes.titleRoot}>
            <Typography id="request-report-title" variant="h6" className={classes.title}>
              {TITLES[wizard.step]}
            </Typography>
            {wizard.step === 'type' && (
              <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
                A shareable company or team overview, or a detailed record on named people.
              </Typography>
            )}
          </DialogTitle>
          <Box className={classes.steps} aria-hidden>
            {wizard.steps.map((step, index) => (
              <i key={step} className={clsx(classes.stepBar, { [classes.stepBarDone]: index <= wizard.stepIndex })} />
            ))}
          </Box>
        </>
      )}

      <DialogContent className={classes.content}>
        {submitted ? (
          <ReportStatusPanel
            report={report}
            timedOut={timedOut}
            pollError={!!pollError}
            notifying={wizard.form.channels.length > 0}
            landed={markLanded}
            onLanded={() => setMarkLanded(true)}
          />
        ) : (
          <>
            {wizard.step === 'type' && <TypeStep wizard={wizard} />}
            {wizard.step === 'scope' && <ScopeStep wizard={wizard} teams={teams} />}
            {wizard.step === 'select' && <PeopleStep wizard={wizard} />}
            {wizard.step === 'content' && <ContentStep wizard={wizard} />}
            {wizard.step === 'qualifications' && <QualificationsStep wizard={wizard} />}
            {wizard.step === 'analysis' && <AnalysisStep wizard={wizard} />}
            {wizard.step === 'review' && <ReviewStep wizard={wizard} teams={teams} companyName={companyName} />}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {submitted ? (
          <>
            <AppButton variant="outlined" size="large" onClick={goToReports}>
              Open Reports
            </AppButton>
            {report?.status === 'completed' && markLanded ? (
              <AppButton
                variant="contained"
                color="primary"
                size="large"
                startIcon={<FiDownload size={16} />}
                loading={downloadingId === report.request_id}
                loaderVariant="paper"
                onClick={() => download(report.request_id)}>
                Download report
              </AppButton>
            ) : report?.status === 'failed' ? (
              <AppButton variant="contained" color="primary" size="large" onClick={wizard.retry}>
                Try again
              </AppButton>
            ) : (
              <AppButton variant="contained" color="primary" size="large" onClick={onClose}>
                Done
              </AppButton>
            )}
          </>
        ) : (
          <>
            <AppButton
              variant="outlined"
              size="large"
              onClick={wizard.stepIndex === 0 ? onClose : wizard.goBack}
              disabled={wizard.submitting}>
              {wizard.stepIndex === 0 ? 'Cancel' : 'Back'}
            </AppButton>
            <AppButton
              variant="contained"
              color="primary"
              size="large"
              disabled={!wizard.canGoNext}
              loading={wizard.submitting}
              loaderVariant="paper"
              onClick={isLastStep ? wizard.submit : wizard.goNext}>
              {isLastStep ? 'Generate report' : 'Continue'}
            </AppButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
